/**
 * useErrorHandler Hook
 * 
 * Hook for handling application errors, particularly 403 Access Denied.
 * Listens for access denied events from api-client.
 */

import { useState, useEffect, useCallback } from 'react';
import { ACCESS_DENIED_EVENT } from '../lib/api-client';

export interface ErrorHandlerState {
  /** Whether to show access denied modal */
  showAccessDenied: boolean;
  /** URL that was denied access */
  deniedUrl?: string;
}

export interface UseErrorHandlerReturn extends ErrorHandlerState {
  /** Handle an error (for manual error handling) */
  handleError: (error: unknown) => void;
  /** Dismiss the current error */
  dismissError: () => void;
  /** Set access denied state manually */
  setAccessDenied: (url?: string) => void;
}

/**
 * Hook for centralized error handling
 */
export function useErrorHandler(): UseErrorHandlerReturn {
  const [state, setState] = useState<ErrorHandlerState>({
    showAccessDenied: false,
    deniedUrl: undefined,
  });

  /**
   * Handle access denied event from api-client
   */
  useEffect(() => {
    const handleAccessDenied = (event: CustomEvent<{ url?: string }>) => {
      setState({
        showAccessDenied: true,
        deniedUrl: event.detail.url,
      });
    };

    window.addEventListener(ACCESS_DENIED_EVENT, handleAccessDenied as EventListener);
    
    return () => {
      window.removeEventListener(ACCESS_DENIED_EVENT, handleAccessDenied as EventListener);
    };
  }, []);

  /**
   * Handle an error manually
   */
  const handleError = useCallback((error: unknown) => {
    // Check for 403 errors
    if (
      error && 
      typeof error === 'object' && 
      'response' in error && 
      (error as { response?: { status?: number } }).response?.status === 403
    ) {
      setState({
        showAccessDenied: true,
        deniedUrl: undefined,
      });
      return;
    }

    // For other errors, could add more handling here
    console.error('Unhandled error:', error);
  }, []);

  /**
   * Dismiss the current error
   */
  const dismissError = useCallback(() => {
    setState({
      showAccessDenied: false,
      deniedUrl: undefined,
    });
  }, []);

  /**
   * Set access denied state manually
   */
  const setAccessDenied = useCallback((url?: string) => {
    setState({
      showAccessDenied: true,
      deniedUrl: url,
    });
  }, []);

  return {
    ...state,
    handleError,
    dismissError,
    setAccessDenied,
  };
}
